import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Check, ChevronDown, ChevronRight, ChevronsUpDown, Home, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { UserMenu } from '@/components/auth/UserMenu';
import { useEquipScopeOptional } from '@/views/equip/context/EquipScopeContext';
import { PERSONA_OPTIONS } from '@/lib/equip-extras';
import { useImpersonation } from '@/context/ImpersonationContext';
import { useEffectiveEmploymentStatus } from '@/hooks/equip/useEffectiveEmploymentStatus';
import { useBreadcrumbs, type BreadcrumbLevel } from '@/views/equip/context/BreadcrumbContext';

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon?: React.ReactNode;
  isAnchor?: boolean;
  /** Sub-items render as a hover dropdown */
  children?: Omit<NavItem, 'children'>[];
}

interface FloatingNavProps {
  /** Nav items to display */
  items: NavItem[];
  /** Optional CTA button */
  cta?: {
    label: string;
    path: string;
    icon?: React.ReactNode;
  };
  /** Show home button. Defaults to true. */
  showHome?: boolean;
  /** Show persona dropdown (admin-only). */
  showPersonaDropdown?: boolean;
  /** Force nav to stay visible regardless of scroll. */
  forceVisible?: boolean;
}

export function FloatingNav({
  items,
  cta,
  showHome,
  showPersonaDropdown,
  forceVisible = false,
}: FloatingNavProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  // Determine if we should show home button
  const shouldShowHome = showHome ?? true;

  // Clinician selector from EquipScopeContext (optional — null outside EquipScopeProvider)
  const scope = useEquipScopeOptional();
  const showClinicianSelector = Boolean(scope?.showClinicianDropdown);

  // State for clinician/manager/physician comboboxes
  const [comboOpen, setComboOpen] = useState(false);
  const [managerComboOpen, setManagerComboOpen] = useState(false);
  const [physicianComboOpen, setPhysicianComboOpen] = useState(false);

  // Persona impersonation selector
  const { selectedPersona, setSelectedPersona, setEmploymentStatusOverride } = useImpersonation();
  const { effectiveEmploymentStatus } = useEffectiveEmploymentStatus();
  const [personaOpen, setPersonaOpen] = useState(false);

  // Breadcrumb trail
  const { trail } = useBreadcrumbs();
  const handleBreadcrumbCollapse = useCallback((level: BreadcrumbLevel) => {
    level.onCollapse();
    if (level.scrollTarget) {
      setTimeout(() => {
        level.scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, []);

  // Route-aware selector: manager on /leadership (admin only), physician on /apc-dashboard (admin only), clinician elsewhere
  const isLeadershipPage = currentPath.startsWith('/leadership');
  const isApcDashboardPage = currentPath.startsWith('/apc-dashboard');
  const showManagerSelector = isLeadershipPage && Boolean(scope?.isAdmin || scope?.isSeniorLead);
  const showPhysicianSelector = isApcDashboardPage && Boolean(scope?.isAdmin);
  const showClinicianSelectorHere = !isLeadershipPage && !isApcDashboardPage && showClinicianSelector;

  // State for hide-on-scroll behavior
  const [isVisible, setIsVisible] = useState(true);
  const isHoveringRef = useRef(false);
  const lastScrollY = useRef(0);
  const scrollThrottleRef = useRef(false);
  const hoverZoneRef = useRef<HTMLDivElement>(null);

  // Track whether forceVisible has ever been true (= slide-based page like Landing)
  const hasBeenForceVisible = useRef(forceVisible);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const INACTIVITY_DELAY = 3000;

  // Start/reset the inactivity auto-hide timer (scroll-based pages only)
  const startInactivityTimer = useCallback(() => {
    if (hasBeenForceVisible.current) return; // slide-based pages don't use this
    clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      if (!isHoveringRef.current && window.scrollY > 50) {
        setIsVisible(false);
      }
    }, INACTIVITY_DELAY);
  }, []);

  // Cleanup inactivity timer on unmount
  useEffect(() => {
    return () => clearTimeout(inactivityTimerRef.current);
  }, []);

  // When forceVisible turns off (slide change on Landing), hide nav unless hovering.
  // Only applies to slide-based pages (where forceVisible has been true).
  useEffect(() => {
    if (forceVisible) {
      hasBeenForceVisible.current = true;
      setIsVisible(true);
    } else if (hasBeenForceVisible.current && !isHoveringRef.current) {
      setIsVisible(false);
    }
  }, [forceVisible]);

  // Handle scroll behavior with throttling
  useEffect(() => {
    const handleScroll = () => {
      if (scrollThrottleRef.current) return;
      scrollThrottleRef.current = true;

      requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;

        // Scrolling down past threshold - hide nav (unless hovering or force visible)
        if (currentScrollY > lastScrollY.current && currentScrollY > 50 && !isHoveringRef.current && !forceVisible) {
          clearTimeout(inactivityTimerRef.current);
          setIsVisible(false);
        }
        // Scrolling up - show nav
        else if (currentScrollY < lastScrollY.current - 10) {
          setIsVisible(true);
          startInactivityTimer();
        }
        // At top of page - always show
        else if (currentScrollY < 50) {
          clearTimeout(inactivityTimerRef.current);
          setIsVisible(true);
        }

        lastScrollY.current = currentScrollY;
        scrollThrottleRef.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [startInactivityTimer]);

  // Handle hover zone for desktop - show on enter, hide on leave
  const handleMouseEnterZone = useCallback(() => {
    isHoveringRef.current = true;
    clearTimeout(inactivityTimerRef.current);
    setIsVisible(true);
  }, []);

  const handleMouseLeaveZone = useCallback(() => {
    isHoveringRef.current = false;
    if (hasBeenForceVisible.current) {
      // Slide-based page (Landing): hide unless currently force-visible
      if (!forceVisible) {
        setIsVisible(false);
      }
    } else {
      // Scroll-based page: hide only if scrolled past top
      if (window.scrollY > 50) {
        setIsVisible(false);
      }
    }
  }, [forceVisible]);

  // Handle anchor link clicks
  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Contrast-agnostic styles using backdrop blur and semi-transparent backgrounds
  // Works on both light and dark backgrounds
  const navContainerStyles = 'bg-black/40 backdrop-blur-xl border-white/20 shadow-lg';
  const navItemStyles = 'text-white/90 hover:text-white hover:bg-white/20';
  const activeItemStyles = 'text-eventus-navy font-semibold';
  const activeBgStyles = 'bg-white';

  return (
    <>
      {/* Desktop: Hover detection zone at top of viewport */}
      <div
        ref={hoverZoneRef}
        className="hidden lg:block fixed top-0 left-0 right-0 h-24 z-40"
        onMouseEnter={handleMouseEnterZone}
        onMouseLeave={handleMouseLeaveZone}
        aria-hidden="true"
      />

      {/* Desktop: Fixed top navigation with animations */}
      <AnimatePresence>
        {isVisible && (
          <motion.nav
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="hidden lg:block lg:fixed lg:top-4 lg:left-1/2 lg:-translate-x-1/2 lg:z-50"
            onMouseEnter={handleMouseEnterZone}
            onMouseLeave={handleMouseLeaveZone}
            aria-label="Main navigation"
          >
            {/* Unified horizontal bar */}
            <div className={`flex items-center gap-1 px-2 py-1.5 rounded-full border ${navContainerStyles}`}>
              {/* Branding */}
              <div className="flex items-center gap-1 px-3 py-2 mr-2 border-r border-white/20 whitespace-nowrap">
                <span className="text-sm font-bold text-white">EQuIP 2026</span>
                <span className="text-xs font-semibold text-eventus-teal">BETA</span>
              </div>
              {/* Page Navigation */}
              {items.map((item) => {
                // Group item with children — hover dropdown
                if (item.children && item.children.length > 0) {
                  const isGroupActive = item.children.some(c => currentPath === c.path);
                  return (
                    <div key={item.id} className="relative group">
                      <button
                        className={`relative flex items-center gap-1.5 px-4 py-2 rounded-full transition-all duration-200 whitespace-nowrap ${
                          isGroupActive ? activeItemStyles : navItemStyles
                        }`}
                      >
                        {isGroupActive && (
                          <motion.div
                            layoutId="activePageNav"
                            className={`absolute inset-0 ${activeBgStyles} rounded-full`}
                            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                          />
                        )}
                        <span className="relative z-10 flex items-center gap-1.5">
                          {item.icon}
                          <span className="text-sm font-medium">{item.label}</span>
                          <ChevronDown className="w-3 h-3 opacity-60" />
                        </span>
                      </button>
                      {/* Dropdown */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-60">
                        <div className="bg-black/80 backdrop-blur-xl border border-white/20 rounded-xl py-1.5 min-w-[160px] shadow-xl">
                          {item.children.map((child) => {
                            const isChildActive = currentPath === child.path;
                            return (
                              <Link
                                key={child.id}
                                to={child.path}
                                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                                  isChildActive
                                    ? 'text-white font-semibold bg-white/15'
                                    : 'text-white/80 hover:text-white hover:bg-white/10'
                                }`}
                              >
                                {child.icon && <span>{child.icon}</span>}
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                const isActive = item.isAnchor ? false : currentPath === item.path;

                if (item.isAnchor) {
                  return (
                    <a
                      key={item.id}
                      href={item.path}
                      onClick={(e) => handleAnchorClick(e, item.path)}
                      className={`relative flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200 ${navItemStyles}`}
                    >
                      {item.icon && <span>{item.icon}</span>}
                      <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
                      isActive ? activeItemStyles : navItemStyles
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activePageNav"
                        className={`absolute inset-0 ${activeBgStyles} rounded-full`}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-2">
                      {item.icon}
                      <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                    </span>
                  </Link>
                );
              })}

              {/* CTA */}
              {cta && (
                <Link
                  to={cta.path}
                  className={`relative flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${navItemStyles}`}
                >
                  {cta.icon}
                  <span className="text-sm font-medium whitespace-nowrap">{cta.label}</span>
                </Link>
              )}

              {/* Persona Selector (admin-only) */}
              {showPersonaDropdown && (
                <>
                  <div className="w-px h-5 bg-white/20 mx-1" aria-hidden="true" />
                  <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
                    <PopoverTrigger asChild>
                      <button
                        role="combobox"
                        aria-expanded={personaOpen}
                        aria-label="Select persona"
                        className={cn(
                          "flex items-center justify-between gap-2 text-sm rounded-full px-3 py-1.5 border focus:outline-none focus:ring-2 focus:ring-white/30 transition-colors min-w-[160px] cursor-pointer",
                          selectedPersona
                            ? "bg-violet-500/20 hover:bg-violet-500/30 text-violet-200 border-violet-400/30"
                            : "bg-white/10 hover:bg-white/20 text-white/90 hover:text-white border-white/20"
                        )}
                      >
                        <span className="truncate">
                          {selectedPersona
                            ? PERSONA_OPTIONS.find(o => JSON.stringify(o.profile) === JSON.stringify(selectedPersona))?.label ?? 'Custom Persona'
                            : 'Select Persona'}
                        </span>
                        <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[240px] p-0 bg-slate-900/95 backdrop-blur-xl border border-white/20 text-white"
                      align="end"
                    >
                      <Command className="bg-transparent [&_[cmdk-input-wrapper]]:border-white/20 [&_[cmdk-input-wrapper]]:border-b">
                        <CommandInput
                          placeholder="Search persona..."
                          className="text-white placeholder:text-white/40"
                        />
                        <CommandList>
                          <CommandEmpty className="py-3 text-center text-sm text-white/50">
                            No persona found.
                          </CommandEmpty>
                          <CommandGroup>
                            {PERSONA_OPTIONS.map((option, i) => {
                              const isActive = JSON.stringify(option.profile) === JSON.stringify(selectedPersona);
                              return (
                                <CommandItem
                                  key={i}
                                  value={option.label}
                                  onSelect={() => {
                                    setSelectedPersona(option.profile);
                                    setPersonaOpen(false);
                                  }}
                                  className="text-white/90 aria-selected:bg-white/10 aria-selected:text-white cursor-pointer"
                                >
                                  {option.label}
                                  <Check className={cn('ml-auto w-4 h-4', isActive ? 'opacity-100' : 'opacity-0')} />
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {/* PT / FT Toggle */}
                  <div className="w-px h-5 bg-white/20 mx-1" aria-hidden="true" />
                  <div className="flex items-center gap-0.5 bg-white/10 rounded-full p-0.5">
                    <button
                      onClick={() => setEmploymentStatusOverride('part-time')}
                      className={cn(
                        'px-2.5 py-1 rounded-full text-xs font-medium transition-all',
                        effectiveEmploymentStatus === 'part-time'
                          ? 'bg-white/20 text-white font-semibold'
                          : 'text-white/50 hover:text-white/70'
                      )}
                    >
                      PT
                    </button>
                    <button
                      onClick={() => setEmploymentStatusOverride('full-time')}
                      className={cn(
                        'px-2.5 py-1 rounded-full text-xs font-medium transition-all',
                        effectiveEmploymentStatus === 'full-time'
                          ? 'bg-white/20 text-white font-semibold'
                          : 'text-white/50 hover:text-white/70'
                      )}
                    >
                      FT
                    </button>
                  </div>
                </>
              )}

              {/* Scope Selector — manager on /leadership (admin), physician on /apc-dashboard (admin), clinician elsewhere */}
              {(showManagerSelector || showClinicianSelectorHere || showPhysicianSelector) && (
                <>
                  <div className="w-px h-5 bg-white/20 mx-1" aria-hidden="true" />
                  <div className="flex items-center gap-2 px-1">
                    {showManagerSelector ? (
                      scope!.isLoadingManagers ? (
                        <span className="text-white/50 text-sm px-2">Loading...</span>
                      ) : (
                        <Popover open={managerComboOpen} onOpenChange={setManagerComboOpen}>
                          <PopoverTrigger asChild>
                            <button
                              role="combobox"
                              aria-expanded={managerComboOpen}
                              aria-label="Select manager"
                              className="flex items-center justify-between gap-2 bg-white/10 hover:bg-white/20 text-white/90 hover:text-white text-sm rounded-full px-3 py-1.5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition-colors min-w-[240px] cursor-pointer"
                            >
                              <span className="truncate">
                                {scope!.selectedManager
                                  ? (scope!.managers.find(m => m.managerEmail === scope!.selectedManager)?.managerFullName || scope!.selectedManager)
                                  : scope!.isSeniorLead ? '— Select Area Director —' : '— Select Manager —'}
                              </span>
                              <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-[280px] p-0 bg-slate-900/95 backdrop-blur-xl border border-white/20 text-white"
                            align="end"
                          >
                            <Command className="bg-transparent [&_[cmdk-input-wrapper]]:border-white/20 [&_[cmdk-input-wrapper]]:border-b">
                              <CommandInput
                                placeholder="Search manager..."
                                className="text-white placeholder:text-white/40"
                              />
                              <CommandList>
                                <CommandEmpty className="py-3 text-center text-sm text-white/50">
                                  No manager found.
                                </CommandEmpty>
                                <CommandGroup>
                                  {!scope!.isSeniorLead && (
                                    <CommandItem
                                      value="__clear__"
                                      onSelect={() => {
                                        scope!.setSelectedManager(null);
                                        setManagerComboOpen(false);
                                      }}
                                      className="text-white/60 aria-selected:bg-white/10 aria-selected:text-white cursor-pointer"
                                    >
                                      — All Managers —
                                      <Check className={cn('ml-auto w-4 h-4', !scope!.selectedManager ? 'opacity-100' : 'opacity-0')} />
                                    </CommandItem>
                                  )}
                                  {scope!.managers.map((m) => (
                                    <CommandItem
                                      key={m.managerEmail}
                                      value={m.managerFullName || m.managerEmail}
                                      onSelect={() => {
                                        scope!.setSelectedManager(m.managerEmail);
                                        setManagerComboOpen(false);
                                      }}
                                      className="text-white/90 aria-selected:bg-white/10 aria-selected:text-white cursor-pointer"
                                    >
                                      {m.managerFullName || m.managerEmail}
                                      <Check className={cn('ml-auto w-4 h-4', scope!.selectedManager === m.managerEmail ? 'opacity-100' : 'opacity-0')} />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )
                    ) : showPhysicianSelector ? (
                      scope!.isLoadingSupervisingPhysicians ? (
                        <span className="text-white/50 text-sm px-2">Loading...</span>
                      ) : (
                        <Popover open={physicianComboOpen} onOpenChange={setPhysicianComboOpen}>
                          <PopoverTrigger asChild>
                            <button
                              role="combobox"
                              aria-expanded={physicianComboOpen}
                              aria-label="Select physician"
                              className="flex items-center justify-between gap-2 bg-white/10 hover:bg-white/20 text-white/90 hover:text-white text-sm rounded-full px-3 py-1.5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition-colors min-w-[240px] cursor-pointer"
                            >
                              <span className="truncate">
                                {scope!.selectedPhysicianNpi
                                  ? (scope!.supervisingPhysicians.find(p => p.npi === scope!.selectedPhysicianNpi)?.name || String(scope!.selectedPhysicianNpi))
                                  : '— Select Physician —'}
                              </span>
                              <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-[280px] p-0 bg-slate-900/95 backdrop-blur-xl border border-white/20 text-white"
                            align="end"
                          >
                            <Command className="bg-transparent [&_[cmdk-input-wrapper]]:border-white/20 [&_[cmdk-input-wrapper]]:border-b">
                              <CommandInput
                                placeholder="Search physician..."
                                className="text-white placeholder:text-white/40"
                              />
                              <CommandList>
                                <CommandEmpty className="py-3 text-center text-sm text-white/50">
                                  No physician found.
                                </CommandEmpty>
                                <CommandGroup>
                                  <CommandItem
                                    value="__clear__"
                                    onSelect={() => {
                                      scope!.setSelectedPhysicianNpi(null);
                                      setPhysicianComboOpen(false);
                                    }}
                                    className="text-white/60 aria-selected:bg-white/10 aria-selected:text-white cursor-pointer"
                                  >
                                    — All Physicians —
                                    <Check className={cn('ml-auto w-4 h-4', !scope!.selectedPhysicianNpi ? 'opacity-100' : 'opacity-0')} />
                                  </CommandItem>
                                  {scope!.supervisingPhysicians.map((p) => (
                                    <CommandItem
                                      key={p.npi}
                                      value={p.name || String(p.npi)}
                                      onSelect={() => {
                                        scope!.setSelectedPhysicianNpi(p.npi);
                                        setPhysicianComboOpen(false);
                                      }}
                                      className="text-white/90 aria-selected:bg-white/10 aria-selected:text-white cursor-pointer"
                                    >
                                      {p.name || String(p.npi)}
                                      <Check className={cn('ml-auto w-4 h-4', scope!.selectedPhysicianNpi === p.npi ? 'opacity-100' : 'opacity-0')} />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )
                    ) : (
                      scope!.isLoadingClinicians ? (
                        <span className="text-white/50 text-sm px-2">Loading...</span>
                      ) : (
                        <Popover open={comboOpen} onOpenChange={setComboOpen}>
                          <PopoverTrigger asChild>
                            <button
                              role="combobox"
                              aria-expanded={comboOpen}
                              aria-label="Select clinician"
                              className="flex items-center justify-between gap-2 bg-white/10 hover:bg-white/20 text-white/90 hover:text-white text-sm rounded-full px-3 py-1.5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition-colors min-w-[240px] cursor-pointer"
                            >
                              <span className="truncate">
                                {scope!.selectedClinician
                                  ? (scope!.clinicians.find(c => c.providerEmail === scope!.selectedClinician)?.providerFullName || scope!.selectedClinician)
                                  : '— Select Clinician —'}
                              </span>
                              <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-[280px] p-0 bg-slate-900/95 backdrop-blur-xl border border-white/20 text-white"
                            align="end"
                          >
                            <Command className="bg-transparent [&_[cmdk-input-wrapper]]:border-white/20 [&_[cmdk-input-wrapper]]:border-b">
                              <CommandInput
                                placeholder="Search clinician..."
                                className="text-white placeholder:text-white/40"
                              />
                              <CommandList>
                                <CommandEmpty className="py-3 text-center text-sm text-white/50">
                                  No clinician found.
                                </CommandEmpty>
                                <CommandGroup>
                                  <CommandItem
                                    value="__clear__"
                                    onSelect={() => {
                                      scope!.setSelectedClinician(null);
                                      setComboOpen(false);
                                    }}
                                    className="text-white/60 aria-selected:bg-white/10 aria-selected:text-white cursor-pointer"
                                  >
                                    — All Clinicians —
                                    <Check className={cn('ml-auto w-4 h-4', !scope!.selectedClinician ? 'opacity-100' : 'opacity-0')} />
                                  </CommandItem>
                                  {scope!.clinicians.map((c) => (
                                    <CommandItem
                                      key={c.providerEmail}
                                      value={c.providerFullName || c.providerEmail}
                                      onSelect={() => {
                                        scope!.setSelectedClinician(c.providerEmail);
                                        setComboOpen(false);
                                      }}
                                      className="text-white/90 aria-selected:bg-white/10 aria-selected:text-white cursor-pointer"
                                    >
                                      {c.providerFullName || c.providerEmail}
                                      <Check className={cn('ml-auto w-4 h-4', scope!.selectedClinician === c.providerEmail ? 'opacity-100' : 'opacity-0')} />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )
                    )}
                  </div>
                </>
              )}

              {/* Breadcrumb pills — visible when sections are expanded */}
              {trail.length > 0 && (
                <>
                  <div className="w-px h-5 bg-white/20 mx-1" aria-hidden="true" />
                  <div className="flex items-center gap-1">
                    {trail.map((level, index) => (
                      <span key={level.id} className="flex items-center gap-1 shrink-0">
                        {index > 0 && (
                          <ChevronRight className="h-3 w-3 text-white/50 mx-0.5" />
                        )}
                        <span className="inline-flex items-center bg-white/15 text-white rounded-full px-2.5 py-1 text-xs font-medium max-w-[130px]">
                          <span className="truncate">{level.label}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBreadcrumbCollapse(level);
                            }}
                            className="ml-1 text-white/60 hover:text-white transition-colors cursor-pointer shrink-0"
                            aria-label={`Collapse ${level.label}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      </span>
                    ))}
                  </div>
                </>
              )}

              {/* Divider before home/user section */}
              <div className="w-px h-5 bg-white/20 mx-1" aria-hidden="true" />

              {/* Home Link */}
              {shouldShowHome && (
                <Link
                  to="/"
                  aria-label="Return to home page"
                  className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${navItemStyles}`}
                >
                  <Home className="w-4 h-4" />
                  <span className="sr-only">Home</span>
                </Link>
              )}

              {/* User Menu */}
              <UserMenu />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Mobile: Bottom navigation */}
      <div className="lg:hidden">
        <nav
          className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-xl border-t border-white/20 z-50 safe-area-inset-bottom"
          aria-label="Mobile navigation"
        >
          <div className="flex justify-around py-2 px-2">
            {/* Home button */}
            {shouldShowHome && (
              <Link
                to="/"
                aria-label="Return to home page"
                className="flex flex-col items-center gap-1 px-3 py-1.5 text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-lg"
              >
                <Home className="w-5 h-5" />
                <span className="text-xs">Home</span>
              </Link>
            )}

            {items.flatMap((item) => {
              // Flatten group children for mobile
              const mobileItems = item.children ? item.children : [item];
              return mobileItems.map((child) => {
                const isActive = child.isAnchor ? false : currentPath === child.path;

                if (child.isAnchor) {
                  return (
                    <a
                      key={child.id}
                      href={child.path}
                      onClick={(e) => handleAnchorClick(e, child.path)}
                      className="flex flex-col items-center gap-1 px-3 py-1.5 text-white/80 rounded-lg"
                    >
                      {child.icon ? (
                        <span className="w-5 h-5 flex items-center justify-center">{child.icon}</span>
                      ) : (
                        <span className="w-5 h-5" />
                      )}
                      <span className="text-xs font-medium">{child.label}</span>
                    </a>
                  );
                }

                return (
                  <Link
                    key={child.id}
                    to={child.path}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex flex-col items-center gap-1 px-3 py-1.5 transition-colors rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${
                      isActive ? 'text-eventus-teal font-semibold' : 'text-white/80'
                    }`}
                  >
                    <div className="relative">
                      {child.icon ? (
                        <span className="w-5 h-5 flex items-center justify-center">{child.icon}</span>
                      ) : (
                        <span className="w-5 h-5" />
                      )}
                      {isActive && (
                        <motion.div
                          layoutId="activeMobileNav"
                          className="absolute -inset-2 rounded-full -z-10 bg-eventus-teal/20 ring-2 ring-eventus-teal/40"
                        />
                      )}
                    </div>
                    <span className="text-xs font-medium">{child.label}</span>
                  </Link>
                );
              });
            })}

            {/* Mobile CTA */}
            {cta && (
              <Link
                to={cta.path}
                className="flex flex-col items-center gap-1 px-3 py-1.5 text-white/80 rounded-lg"
              >
                <span className="w-5 h-5" />
                <span className="text-xs font-medium">{cta.label}</span>
              </Link>
            )}
          </div>
        </nav>
      </div>
    </>
  );
}
